"use client";

import { useEffect, useState } from "react";

export interface KeyboardViewport {
  /** Height (px) the virtual keyboard occupies: innerHeight - visual height -
   *  offsetTop. Doubles as the distance from the layout bottom to the visible
   *  area bottom, so it's the correct `bottom` to lift an element to the
   *  keyboard top on both Android and iOS. `0` when the keyboard is closed. */
  inset: number;
  /** `visualViewport.height` — the exact visible area above the keyboard. More
   *  reliable than `100dvh` on iOS, where dvh ignores the keyboard. */
  height: number;
  /** `visualViewport.offsetTop` — > 0 on iOS when Safari scrolls the page to
   *  reveal the focused input. `0` on Android (resizes-visual). */
  offsetTop: number;
}

const CLOSED: KeyboardViewport = { inset: 0, height: 0, offsetTop: 0 };

/**
 * Tracks the virtual keyboard on mobile via `window.visualViewport`, exposing
 * the keyboard `inset`, the visible `height`, and the page-scroll `offsetTop`.
 * Returns zeros when the keyboard is closed, disabled, in SSR, or when the API
 * is unavailable.
 *
 * Used to resize/position drawers around the keyboard without vaul's
 * `repositionInputs`, which mutates the DOM imperatively and fights manual
 * positioning.
 */
export function useKeyboardInset(enabled: boolean): KeyboardViewport {
  const [value, setValue] = useState<KeyboardViewport>(CLOSED);

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !window.visualViewport) {
      setValue(CLOSED);
      return;
    }

    const viewport = window.visualViewport;

    const update = () => {
      const offsetTop = Math.max(0, Math.round(viewport.offsetTop));
      const height = Math.round(viewport.height);
      const inset = Math.max(
        0,
        Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
      );
      // Skip re-render when unchanged: switching inputs with the keyboard
      // already open keeps the same values (and a steady drawer).
      setValue((prev) =>
        prev.inset === inset &&
        prev.height === height &&
        prev.offsetTop === offsetTop
          ? prev
          : { inset, height, offsetTop },
      );
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);

    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [enabled]);

  return value;
}
